import { HttpErrorResponse, HttpRequest } from '@angular/common/http';

import { environment } from '../../../environments/environment';

/**
 * Fachada sobre el SDK de Sentry.
 *
 * El SDK se carga como asset (assets/js/sentry.min.js), no como dependencia de npm, igual que en
 * clvsccpzposui: servido desde src/assets el archivo se copia verbatim, sin pasar por el
 * compilador ni por el minificador. Ver el <script> en src/index.html.
 *
 * Este es el unico punto del codigo que toca el global window.Sentry, y todo va guardado: si el
 * script no cargo, estas funciones no hacen nada y la aplicacion sigue operando.
 */
function sdk(): any {
  return (window as any).Sentry;
}

/**
 * Ejecuta una llamada al SDK sin dejar que un fallo suyo se propague. El monitoreo nunca debe ser
 * la causa de una caida, y aca cada punto de entrada esta en un lugar delicado:
 *
 * - initSentry corre antes de bootstrapModule; una excepcion ahi deja la app sin arrancar.
 * - captureApiError corre dentro del catchError del interceptor; si revienta, el componente nunca
 *   recibe el error y el spinner se queda pegado.
 * - setSentryUser corre en el login, el logout y el arranque de AppComponent; si revienta, se
 *   corta ese flujo.
 */
function safely(action: () => void): void {
  try {
    action();
  } catch (e) {
    console.error('Sentry fallo', e);
  }
}

/** Largo maximo del mensaje del API que se adjunta al issue. */
const MAX_MESSAGE_LENGTH = 500;

/**
 * Arranca Sentry. Se llama desde main.ts antes del bootstrap de Angular.
 */
export function initSentry(): void {
  const SENTRY = sdk();

  if (!SENTRY || !environment.production) { return; }

  safely(() => SENTRY.init({
    dsn: environment.SentryDsn,
    environment: environment.env,
    integrations: [
      SENTRY.replayIntegration({
        // Se quiere ver todo lo que el usuario digita y ve durante la sesion. El SDK igual
        // enmascara por su cuenta los input[type=password] y los campos con autocomplete
        // cc-number / cc-exp / current-password / new-password; eso no se puede apagar.
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
        // Cuerpo completo de las llamadas. Los headers no se capturan a proposito: ahi viajan
        // los Authorization: Bearer que inyecta AppInterceptor.
        networkDetailAllowUrls: [/.*/],
        networkCaptureBodies: true
      })
    ],
    // Grabar todas las sesiones, como hacia LogRocket. Si la cuota de replays se queda corta,
    // bajar este valor (~0.2) y dejar replaysOnErrorSampleRate en 1.0: se conserva el video de
    // todos los errores y solo se recortan las sesiones sanas.
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0
  }));
}

/**
 * Reporta un error de JavaScript. Si Sentry no esta inicializado (ambientes que no son
 * produccion) la llamada no hace nada.
 */
export function captureException(error: any): void {
  const SENTRY = sdk();

  if (!SENTRY) { return; }

  safely(() => SENTRY.captureException(error));
}

/**
 * Identifica la sesion para que los issues y los replays dejen de aparecer como "Anonymous User".
 * Recibe null en el logout, para dejar de atribuirle eventos al usuario que salio.
 *
 * Se copian campos explicitos a proposito: el objeto de sesion (HTHCurrentUser) tambien guarda
 * el access_token, y ese no debe salir del navegador.
 */
export function setSentryUser(session: any): void {
  const SENTRY = sdk();

  if (!SENTRY) { return; }

  safely(() => {
    if (!session) {
      SENTRY.setUser(null);

      return;
    }

    SENTRY.setUser({
      username: session.UserName,
      email: session.UserEmail
    });
  });
}

/**
 * Reporta un error no controlado del API, con el detalle de la peticion que lo produjo.
 */
export function captureApiError(request: HttpRequest<any>, err: HttpErrorResponse): void {
  const SENTRY = sdk();

  if (!SENTRY) { return; }

  const PATH = pathOf(request.url);

  safely(() => SENTRY.withScope((scope: any) => {
    scope.setTag('source', 'api');
    scope.setTag('http.status', String(err.status));
    scope.setContext('request', { method: request.method, path: PATH, host: hostOf(request.url) });
    scope.setContext('response', {
      status: err.status,
      statusText: err.statusText,
      message: messageOf(err)
    });
    // Un issue por (metodo, ruta, status). Sin esto Sentry agrupa por el stack, que siempre es
    // el de este interceptor, y todos los errores del API caerian en un unico issue.
    scope.setFingerprint(['api', request.method, PATH, String(err.status)]);
    // Un HttpErrorResponse no es un Error: pasarlo directo produce el inutil
    // "Non-Error exception captured". El Error sintetico da un titulo legible.
    SENTRY.captureException(new Error(`API ${err.status} ${request.method} ${PATH}`));
  }));
}

/**
 * Solo el pathname: si entrara el query string, cada id generaria un issue distinto. El detalle
 * completo de la llamada igual queda grabado en el replay.
 */
function pathOf(url: string): string {
  try {
    return new URL(url).pathname;
  } catch (e) {
    return url; // urls relativas, como las de src/assets
  }
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch (e) {
    return '';
  }
}

function messageOf(err: HttpErrorResponse): string {
  const BODY = err.error;

  if (!BODY) { return err.message || ''; }
  if (typeof BODY === 'string') { return BODY.slice(0, MAX_MESSAGE_LENGTH); }

  const MESSAGE = BODY.Message || BODY.ExceptionMessage || BODY.error_description || BODY.error || err.message || '';

  return String(MESSAGE).slice(0, MAX_MESSAGE_LENGTH);
}
